import React, {
  useState,
  useCallback,
  useEffect,
} from "react";
import { NodeViewWrapper } from "@tiptap/react";
import { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { Editor } from "@tiptap/core";
import { cn } from '@/lib/utils'
import { getStepAction } from "@deepguide-ai/shared";
import {
  ZoomInIcon,
  ZoomOutIcon,
  Trash2Icon,
  AlignLeft,
  AlignCenter,
  AlignRight,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "./plate-ui/dropdown-menu";
// import { trpc } from '../../../../../web/src/lib/trpc'
import { InteractionPoint, ScreenshotAttributes } from "./screenshot-extension";
import { useScreenshotStepData } from "@/tiptap/hooks/useExtensionData";
import { ScreenshotRenderer } from "./shared/screenshot-renderer";
import {
  screenshotToolbarButtonClassName,
  screenshotToolbarClassName,
} from "./shared/screenshot-toolbar-styles";

// Toolbar Component (adapted from screenshot-element-toolbar.tsx)
const ScreenshotNodeViewToolbar = ({
  zoomLevel,
  onZoomChange,
  onAlignChange,
  align,
  onDelete,
  readOnly,
}: {
  zoomLevel: number;
  onZoomChange: (zoom: number) => void;
  onAlignChange: (align: "left" | "center" | "right") => void;
  align: "left" | "center" | "right";
  onDelete: () => void;
  readOnly: boolean;
}) => {
  const alignItems = [
    { icon: AlignLeft, value: "left" },
    { icon: AlignCenter, value: "center" },
    { icon: AlignRight, value: "right" },
  ];
  const IconValue =
    alignItems.find((item) => item.value === align)?.icon ?? AlignCenter;

  if (readOnly) {
    return (
      <div
        className={screenshotToolbarClassName}
        contentEditable={false}
      >
        <button
          type="button"
          onClick={() => onZoomChange(Math.max(1, zoomLevel - 0.25))}
          title="Zoom out"
          disabled={zoomLevel <= 1}
          className={cn(
            screenshotToolbarButtonClassName,
            zoomLevel <= 1 && "pointer-events-none opacity-50"
          )}
        >
          <ZoomOutIcon className="size-[18px]" />
        </button>
        <button
          type="button"
          onClick={() => onZoomChange(Math.min(10, zoomLevel + 0.25))}
          title="Zoom in"
          disabled={zoomLevel >= 10}
          className={cn(
            screenshotToolbarButtonClassName,
            zoomLevel >= 10 && "pointer-events-none opacity-50"
          )}
        >
          <ZoomInIcon className="size-[18px]" />
        </button>
      </div>
    );
  }

  return (
    <div
      className={screenshotToolbarClassName}
      contentEditable={false}
    >
      <button
        type="button"
        onClick={() => onZoomChange(Math.max(1, zoomLevel - 0.25))}
        title="Zoom out"
        disabled={zoomLevel <= 1}
        className={cn(
          screenshotToolbarButtonClassName,
          zoomLevel <= 1 && "pointer-events-none opacity-50"
        )}
      >
        <ZoomOutIcon className="size-[18px]" />
      </button>
      <button
        type="button"
        onClick={() => onZoomChange(Math.min(10, zoomLevel + 0.25))}
        title="Zoom in"
        disabled={zoomLevel >= 10}
        className={cn(
          screenshotToolbarButtonClassName,
          zoomLevel >= 10 && "pointer-events-none opacity-50"
        )}
      >
        <ZoomInIcon className="size-[18px]" />
      </button>

      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={screenshotToolbarButtonClassName}
            title="Align"
          >
            <IconValue className="size-[18px]" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="min-w-0 rounded-md border border-sidebar-border bg-white p-1 shadow-[0_6px_18px_rgba(15,23,42,0.16)]"
          align="end"
        >
          <DropdownMenuRadioGroup
            className="flex flex-col gap-1 hover:bg-transparent"
            value={align}
            onValueChange={(value) =>
              onAlignChange(value as "left" | "center" | "right")
            }
          >
            {alignItems.map(({ icon: Icon, value: itemValue }) => (
              <DropdownMenuRadioItem
                key={itemValue}
                className="flex size-8 items-center justify-center rounded-md p-0 text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground data-[state=checked]:bg-sidebar-accent data-[state=checked]:text-sidebar-foreground"
                value={itemValue}
                hideIcon
              >
                <Icon className="size-[18px]" />
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      <button
        type="button"
        onClick={onDelete}
        title="Remove screenshot"
        className={cn(
          screenshotToolbarButtonClassName,
          "hover:bg-red-50 hover:text-red-600"
        )}
      >
        <Trash2Icon className="size-[18px]" />
      </button>
    </div>
  );
};

// Main NodeView Component
export const ScreenshotNodeView = ({
  node,
  editor,
  updateAttributes,
  deleteNode,
  selected,
}: {
  node: ProseMirrorNode;
  editor: Editor;
  getPos: () => number | undefined;
  updateAttributes: (attributes: Partial<ScreenshotAttributes>) => void;
  deleteNode: () => void;
  selected: boolean;
}) => {
  const attrs = node.attrs as ScreenshotAttributes;
  const {
    url,
    step_id,
    width,
    height,
    zoomLevel = 1,
    align = "center",
    elementBox,
    interactionPoints: serializedInteractionPoints,
  } = attrs;

  
  // Stable states to prevent flickering during streaming
  const [stableUrl, setStableUrl] = useState(url || '');
  const [stableInteractionPoints, setStableInteractionPoints] = useState<InteractionPoint[]>([]);
  const [stableWidth, setStableWidth] = useState(width || 800);
  const [stableHeight, setStableHeight] = useState(height || 600);
  const [stableElementBox, setStableElementBox] = useState(elementBox);
  const [, setEditableVersion] = useState(0);

  useEffect(() => {
    const handleEditableChange = () => {
      setEditableVersion((version) => version + 1);
    };

    (editor as any).on('editableChange', handleEditableChange);
    return () => {
      (editor as any).off('editableChange', handleEditableChange);
    };
  }, [editor]);

  // Update stable states only when we get valid values
  useEffect(() => {
    if (url && url !== stableUrl) {
      setStableUrl(url);
    }
    if (width && width !== stableWidth) {
      setStableWidth(width);
    }
    if (height && height !== stableHeight) {
      setStableHeight(height);
    }
    if (elementBox && elementBox !== stableElementBox) {
      setStableElementBox(elementBox);
    }
  }, [url, stableUrl, width, stableWidth, height, stableHeight, elementBox, stableElementBox]);
  // gjnN4CBlNTHx50jJqAEeo
  // Only fetch when step_id looks like a DB ID (avoid human-friendly IDs like "step-save-todo")
  const isLikelyDbId = !!step_id && (step_id.length === 21 || step_id.startsWith('step-')) // && /^[a-z0-9\\-_]{21,}$/.test(step_id);
  const shouldFetchStep = !!step_id && isLikelyDbId && !stableUrl;
  
  // Use the useScreenshotStepData hook to get step data
  // Use global step resolution since selectedTopicId isn't necessary for screenshots
  const { step, isLoading: isLoadingStep } = useScreenshotStepData({
    stepId: step_id || undefined,
    enabled: shouldFetchStep,
  });

  // Hydrate attributes from fetched step data
  useEffect(() => {
    if (step && !stableUrl) {
      const newAttrs: Partial<ScreenshotAttributes> = {};

      if (step.screenshot?.url) {
        newAttrs.url = step.screenshot.url;
        newAttrs.width = step.screenshot.width ?? 800;
        newAttrs.height = step.screenshot.height ?? 600;
        setStableWidth(step.screenshot.width ?? 800);
        setStableHeight(step.screenshot.height ?? 600);
      }
      if (step.viewportCoordinates) {
        newAttrs.elementBox = step.viewportCoordinates;
        setStableElementBox(step.viewportCoordinates);
      }
      if (step.viewportCoordinates && step.viewportInfo) {
        const interactionPoints = [
          {
            id: step.id,
            type: step.action || "Click",
            x: step.viewportCoordinates.center.x,
            y: step.viewportCoordinates.center.y,
            width: step.viewportCoordinates.width,
            height: step.viewportCoordinates.height,
            xPercent:
              (step.viewportCoordinates.center.x / step.viewportInfo.width) *
              100,
            yPercent:
              (step.viewportCoordinates.center.y / step.viewportInfo.height) *
              100,
            widthPercent:
              (step.viewportCoordinates.width / step.viewportInfo.width) * 100,
            heightPercent:
              (step.viewportCoordinates.height / step.viewportInfo.height) *
              100,
            label: getStepAction(step) ?? "",
          },
        ];
        newAttrs.interactionPoints = interactionPoints;
        setStableInteractionPoints(interactionPoints);
      }
      if (Object.keys(newAttrs).length > 0) {
        updateAttributes(newAttrs);
      }
    }
  }, [step, stableUrl, updateAttributes]);

  const handleZoomChange = useCallback(
    (newZoomLevel: number) => {
      const clampedZoomLevel = Math.max(1, Math.min(10, newZoomLevel));
      updateAttributes({ zoomLevel: clampedZoomLevel });
    },
    [updateAttributes]
  );

  const handleAlignChange = useCallback(
    (newAlign: "left" | "center" | "right") => {
      updateAttributes({ align: newAlign });
    },
    [updateAttributes]
  );

  const interactionPoints =
    serializedInteractionPoints || stableInteractionPoints || [];

  const handleWidthChange = useCallback(
    (newWidth: number) => {
      const newHeight = (newWidth / stableWidth) * stableHeight;
      updateAttributes({ width: newWidth, height: newHeight });
      setStableWidth(newWidth);
      setStableHeight(newHeight);
    },
    [stableWidth, stableHeight, updateAttributes]
  );

  if (!stableUrl) {
    return (
      <NodeViewWrapper
        className={cn(
          "screenshot-container relative my-4 flex",
          {
            "justify-center": align === "center",
            "justify-start": align === "left",
            "justify-end": align === "right",
          },
          selected && "ProseMirror-selectednode"
        )}
      >
        <div
          className="screenshot-placeholder bg-muted/40 border-2 border-dashed border-border rounded-lg flex items-center justify-center text-muted-foreground p-8"
          style={{
            width: "100%",
            maxWidth: 800,
            minHeight: 200,
          }}
        >
          <div className="text-center">
            <div className="text-sm font-medium">
              {isLoadingStep ? "Loading Screenshot..." : "No screenshot available"}
            </div>
            {step_id && (
              <div className="text-xs text-muted-foreground mt-1">
                Step ID: {step_id}
              </div>
            )}
          </div>
        </div>
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper
      className={cn(
        "screenshot-container relative my-4 flex",
        {
          "justify-center": align === "center",
          "justify-start": align === "left",
          "justify-end": align === "right",
        },
        selected && "ProseMirror-selectednode"
      )}
      data-drag-handle
    >
      <div className="relative">
        <ScreenshotRenderer
          url={stableUrl}
          width={stableWidth}
          height={stableHeight}
          elementBox={stableElementBox}
          interactionPoints={interactionPoints}
          cropStyle={attrs.cropStyle || 'full'}
          align={align}
          alt={attrs.alt || ""}
          zoomLevel={zoomLevel}
          onZoomChange={handleZoomChange}
          onAlignChange={handleAlignChange}
          onWidthChange={handleWidthChange}
          readOnly={!editor.isEditable}
          isFullScreenshot={true}
        />
        <ScreenshotNodeViewToolbar
          zoomLevel={zoomLevel}
          onZoomChange={handleZoomChange}
          onAlignChange={handleAlignChange}
          align={align}
          onDelete={deleteNode}
          readOnly={editor.isEditable === false}
        />
      </div>
    </NodeViewWrapper>
  );
};
